import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import './PlaceIndexPage.css';
import { useDeletePlaceMutation, useGetPlaceQuery } from '../generated/graphql';
import { Loading } from '../Loading';
import { formatDate } from '../utils/DateHelper';
import { AuthButton } from '../components/Button';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import { ToastContext } from '../components/Toast';
import { NotSet, textOrNotSet } from '../components/NotSet';
import { SectionHeader } from '../components/SectionHeader';
import { TruncatedTagList } from '../components/TruncatedTagList';

type IPlaceIndexPageProps = RouteComponentProps<{ placeId: string }>;

/** 基本情報で札として並べるタグの数。タグは短いので多めに出し、残りは「他 N 件」にまとめる */
const VISIBLE_TAG_COUNT = 8;

/** 基本情報で札として並べる作業者の数。名前とIDで長いので少なめに出し、残りは「他 N 人」にまとめる */
const VISIBLE_WORKER_COUNT = 5;

/**
 * 物件・基本情報ページ
 */
export const PlaceIndexPage: React.FC<IPlaceIndexPageProps> = function (props) {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const placeId = parseInt(props.match.params.placeId);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const { data, loading, error } = useGetPlaceQuery({
    variables: { placeId },
  });
  // TODO: useGetEnclosingSvgQueryみたいなので現在のPlaceに紐づけられたsvgをすべて取得する
  const [deletePlace, deletePlaceResult] = useDeletePlaceMutation({
    onCompleted() {
      toastContext.showToast('「' + data?.getPlace.name + '」を削除しました', 'success');
      history.push('/places');
    },
    onError() {
      // 削除に失敗しても画面は残し、続けて操作できるようにする
      toastContext.showToast('物件を削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });

  if (error) {
    return <GraphQLErrorDialog error={error} />;
  }

  if (loading || !data) {
    return <Loading />;
  }

  const place = data.getPlace;

  return (
    <div className="PlaceIndexPage-container">
      <Loading open={deletePlaceResult.loading} />
      <ConfirmDialog
        open={confirmDelete}
        title="物件の削除"
        message={<p>「{place.name}」を削除しますか？</p>}
        yesLabel="削除"
        danger={true}
        onYes={() => {
          setConfirmDelete(false);
          deletePlace({ variables: { placeId } });
        }}
        onNo={() => setConfirmDelete(false)}
      />
      <SectionHeader
        title="基本情報"
        actions={
          <>
            <AuthButton
              authLv={2}
              color="white"
              mini={true}
              onClick={() => history.push(`/places/${placeId}/edit`)}
            >
              編集
            </AuthButton>
            <AuthButton
              authLv={2}
              color="red"
              mini={true}
              onClick={() => setConfirmDelete(true)}
            >
              削除
            </AuthButton>
          </>
        }
      />
      <dl className="PlaceIndexPage-details">
        {/* 値が無い項目も行を出し、灰色の「未設定」で実際の値と見分けられるようにする */}
        <dt>タグ</dt>
        <dd>
          {(function () {
            if (place.tags.length === 0) {
              return <NotSet />;
            }
            return (
              <TruncatedTagList
                items={place.tags.map((tag) => ({ key: tag, label: tag }))}
                visibleCount={VISIBLE_TAG_COUNT}
                unit="件"
              />
            );
          })()}
        </dd>
        <dt>責任者</dt>
        <dd>{textOrNotSet(place.chief_name)}</dd>
        <dt>作業者一覧</dt>
        <dd>
          {(function () {
            if (place.authorized_workers.length === 0) {
              return <NotSet />;
            }

            // 作業者もタグと同じ札で並べ、多いときは「他 N 人」にまとめる。同じ名前の人を見分けられるよう、名前にIDを添える
            return (
              <TruncatedTagList
                items={place.authorized_workers.map((worker) => ({
                  key: `${worker.id}`,
                  label: `${worker.name} (${worker.screen_id})`,
                }))}
                visibleCount={VISIBLE_WORKER_COUNT}
                unit="人"
              />
            );
          })()}
        </dd>
        <dt>お客様</dt>
        <dd>{textOrNotSet(place.client_name)}</dd>
        <dt>写真レイヤの画層色</dt>
        <dd>
          {(function () {
            const pictureLayerColor = place.picture_layer_color ?? '#FFFFFF';
            return (
              <div className="PlaceIndexPage-picture_layer_color">
                <div
                  className="PlaceIndexPage-picture_layer_color_box"
                  style={{ backgroundColor: pictureLayerColor }}
                ></div>
                <div className="PlaceIndexPage-picture_layer_color_hex">{pictureLayerColor}</div>
              </div>
            );
          })()}
        </dd>
        <dt>作成日</dt>
        <dd>{formatDate(place.created)}</dd>
        <dt>更新日</dt>
        <dd>{formatDate(place.updated)}</dd>
      </dl>
    </div>
  );
};
